import { useParams, Link, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import { serviceDetails, serviceCategories } from '../../../mocks/servicesPage';
import SeoHead from '../../../components/SeoHead';

const FOREX_TITLES = [
  'Seychelles Forex License',
  'Vanuatu Forex License',
  'Mauritius Forex License',
  'Comoros Forex License',
  'Kenya Forex License',
  'CySEC Forex License (CIF)',
];

const IGAMING_TITLES = [
  'Liberia iGaming License',
  'Curaçao iGaming License',
  'Anjouan iGaming License',
  'Costa Rica iGaming License',
];

const FLAG_MAP: Record<string, string> = {
  'Seychelles Forex License': 'sc',
  'Vanuatu Forex License': 'vu',
  'Mauritius Forex License': 'mu',
  'Comoros Forex License': 'km',
  'Kenya Forex License': 'ke',
  'CySEC Forex License (CIF)': 'cy',
  'Liberia iGaming License': 'lr',
  'Curaçao iGaming License': 'cw',
  'Anjouan iGaming License': 'km',
  'Costa Rica iGaming License': 'cr',
};

// ── Per-service SEO config ──────────────────────────────────────────────────
interface ServiceSeo {
  title: string;
  description: string;
  keywords: string;
}

const SERVICE_SEO: Record<string, ServiceSeo> = {
  'igaming-fx-licensing': {
    title: 'iGaming & Forex Licensing Cyprus | CJ Solutions',
    description:
      'Buy a ready-issued iGaming or Forex license in Cyprus and offshore jurisdictions. CySEC CIF, Curaçao, Vanuatu, Seychelles & more. Fast transfer, verified licenses — CJ Solutions Limassol.',
    keywords:
      'iGaming licensing Cyprus, Forex license Cyprus, CySEC CIF license, Curaçao iGaming license, buy forex license, iGaming license marketplace Limassol, offshore gaming license',
  },
  'aml-kyc-outsourcing': {
    title: 'AML & KYC Outsourcing Cyprus | CJ Solutions',
    description:
      'Expert AML consulting and KYC outsourcing in Limassol, Cyprus. CDD, EDD, sanctions screening, MOKAS reporting & transaction monitoring for CySEC-regulated firms and gaming operators.',
    keywords:
      'AML consulting Limassol, KYC outsourcing Cyprus, AML compliance Cyprus, MOKAS reporting, sanctions screening Cyprus, anti-money laundering Limassol, KYC services CySEC',
  },
  'compliance-outsourcing': {
    title: 'Compliance Outsourcing Cyprus | CJ Solutions',
    description:
      'Outsourced compliance officer and MLRO services for CySEC-regulated firms in Cyprus. MiFID II, GDPR, DORA, FIAU compliance and regulatory reporting — based in Limassol.',
    keywords:
      'compliance outsourcing Cyprus, CySEC compliance officer, MLRO services Limassol, MiFID II compliance Cyprus, regulatory compliance Limassol, FIAU compliance Cyprus, GDPR compliance Cyprus',
  },
  'tax-payroll-outsourcing': {
    title: 'Tax & Payroll Outsourcing Cyprus | CJ Solutions',
    description:
      'Corporate tax planning, VAT compliance, and payroll outsourcing in Cyprus. Leverage the 12.5% corporate tax rate, IP Box regime, and non-dom status — expert advisors in Limassol.',
    keywords:
      'tax advisory Cyprus, payroll outsourcing Limassol, corporate tax Cyprus 12.5%, IP Box Cyprus, non-dom Cyprus, VAT compliance Cyprus, Cyprus tax planning Limassol',
  },
  'accounting-outsourcing': {
    title: 'Accounting Outsourcing Cyprus | CJ Solutions',
    description:
      'Professional accounting outsourcing and IFRS bookkeeping for Cyprus companies. Financial reporting, management accounts, audit coordination and year-end accounts — Limassol-based team.',
    keywords:
      'accounting outsourcing Cyprus, bookkeeping Limassol, IFRS accounting Cyprus, financial reporting Cyprus, management accounts Limassol, audit coordination Cyprus, CySEC accounting',
  },
  'legal-drafting-outsourcing': {
    title: 'Legal Drafting & AML Policies Cyprus | CJ Solutions',
    description:
      'Specialist legal drafting for Cyprus businesses — AML/KYC policies, GDPR privacy policies, employment contracts, shareholder agreements and MiFID II compliance manuals. Limassol-based.',
    keywords:
      'legal drafting Cyprus, AML policy Cyprus, compliance documents Limassol, GDPR privacy policy Cyprus, employment contracts Cyprus, shareholder agreement Cyprus, MiFID II compliance manual',
  },
  'company-formation': {
    title: 'Cyprus Company Formation | CJ Solutions',
    description:
      'Fast Cyprus company formation in 5–7 days. 12.5% corporate tax, EU passporting, nominee directors and registered office in Limassol. Malta and UK company registration also available.',
    keywords:
      'Cyprus company formation, register company Cyprus, company registration Limassol, Cyprus holding company, nominee director Cyprus, registered office Limassol, Malta company formation',
  },
};
// ───────────────────────────────────────────────────────────────────────────

const ServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [licenseTab, setLicenseTab] = useState<'forex' | 'igaming'>('forex');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug || !serviceDetails[slug as keyof typeof serviceDetails]) {
    return <Navigate to="/services" replace />;
  }

  const service = serviceDetails[slug as keyof typeof serviceDetails];
  const otherServices = serviceCategories.filter((s) => s.id !== slug).slice(0, 3);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com';

  // Resolve per-service SEO or fall back to generic
  const seo: ServiceSeo = SERVICE_SEO[slug] ?? {
    title: `${service.title} | CJ Solutions Ltd Cyprus`,
    description: service.subtitle || service.description,
    keywords: `${service.title}, CJ Solutions, Cyprus compliance, licensing services`,
  };

  const serviceJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.title,
      description: service.description,
      url: `${siteUrl}/services/${slug}`,
      areaServed: [
        { '@type': 'Place', name: 'Cyprus' },
        { '@type': 'Place', name: 'Limassol' },
      ],
      provider: {
        '@type': 'LocalBusiness',
        name: 'CJ Solutions Ltd',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Limassol',
          addressRegion: 'Limassol District',
          addressCountry: 'CY',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 34.6786,
          longitude: 33.0413,
        },
      },
      ...(seo.keywords && {
        keywords: seo.keywords,
      }),
    },
    ...(service.faqs && service.faqs.length > 0
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: service.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          },
        ]
      : []),
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: service.title, item: `${siteUrl}/services/${slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={`/services/${slug}`}
        jsonLd={serviceJsonLd}
      />
      <Header isScrolled={isScrolled} />

      {/* ── Hero ── */}
      <div className="relative h-[580px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={`${service.title} — CJ Solutions Cyprus`}
            width={1920}
            height={580}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-900/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
        </div>

        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-amber-500 to-transparent opacity-70"></div>

        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5 bg-amber-500"></div>
                <Link
                  to="/services"
                  className="text-amber-400 text-sm font-semibold uppercase tracking-widest hover:text-amber-300 transition-colors"
                >
                  Our Services
                </Link>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-5">
                {service.title}
              </h1>
              <p className="text-lg lg:text-xl text-white/75 leading-relaxed max-w-2xl mb-8">
                {service.subtitle}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-amber-500/30 whitespace-nowrap"
              >
                Book a Free Consultation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 right-8 hidden sm:block">
          <div className="bg-black/30 backdrop-blur-sm border border-white/20 rounded-t-xl px-6 py-3">
            <nav className="flex items-center gap-2 text-sm font-medium">
              <Link to="/" className="text-white/60 hover:text-amber-400 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-white/30"></i>
              <Link to="/services" className="text-white/60 hover:text-amber-400 transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line text-white/30"></i>
              <span className="text-white">{service.title}</span>
            </nav>
          </div>
        </div>
      </div>

      {/* ── Overview ── */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5 bg-amber-500"></div>
                <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Overview</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                What We Offer
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-5">
                {service.description}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {service.longDescription}
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-slate-900 rounded-2xl p-8 text-white sticky top-28">
                <h3 className="text-lg font-bold mb-6 text-white/90 uppercase tracking-wide">
                  Services at a Glance
                </h3>
                <ul className="space-y-3">
                  {service.subServices.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-amber-400 text-base"></i>
                      </div>
                      <span className="text-white/75 text-sm leading-snug">{s.title}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <Link
                    to="/contact"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                  >
                    Get a Free Quote
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Cyprus ── */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-amber-50/40" style={{contentVisibility: 'auto', containIntrinsicSize: '0 600px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-amber-500"></div>
              <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Why Cyprus</span>
              <div className="w-8 h-0.5 bg-amber-500"></div>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 max-w-2xl mx-auto leading-tight">
              {service.whyCyprus.title}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {service.whyCyprus.benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md hover:border-amber-200 transition-all duration-300 flex gap-5"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sub-Services ── */}
      <section className="py-20 lg:py-28 bg-white" style={{contentVisibility: 'auto', containIntrinsicSize: '0 800px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-0.5 bg-amber-500"></div>
            <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">What's Included</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8 leading-tight max-w-xl">
            Our Full Range of Services
          </h2>

          {slug === 'igaming-fx-licensing' && (
            <div className="flex items-center gap-2 mb-10 bg-slate-100 rounded-full p-1 w-fit">
              <button
                onClick={() => setLicenseTab('forex')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  licenseTab === 'forex'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-line-chart-line text-base"></i>
                  Forex Licenses
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${licenseTab === 'forex' ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-600'}`}>
                    6
                  </span>
                </span>
              </button>
              <button
                onClick={() => setLicenseTab('igaming')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  licenseTab === 'igaming'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-gamepad-line text-base"></i>
                  iGaming Licenses
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${licenseTab === 'igaming' ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-600'}`}>
                    4
                  </span>
                </span>
              </button>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {service.subServices
              .filter((subService) => {
                if (slug !== 'igaming-fx-licensing') return true;
                if (licenseTab === 'forex') return FOREX_TITLES.includes(subService.title);
                return IGAMING_TITLES.includes(subService.title);
              })
              .map((subService, index) => {
                const countryCode = FLAG_MAP[subService.title];
                return (
                  <div
                    key={index}
                    className="group bg-slate-50 border border-slate-200 rounded-xl p-6 hover:bg-slate-900 hover:border-slate-900 transition-all duration-300 cursor-default"
                  >
                    <div className="w-11 h-11 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-4 group-hover:border-amber-500/40 transition-all duration-300 overflow-hidden shadow-sm">
                      {countryCode ? (
                        <img
                          src={`https://flagcdn.com/w40/${countryCode}.png`}
                          srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
                          alt={`${subService.title} flag`}
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (subService as { flag?: string }).flag ? (
                        <span className="text-2xl leading-none select-none">
                          {(subService as { flag?: string }).flag}
                        </span>
                      ) : (
                        <i className={`${subService.icon} text-xl text-slate-600 group-hover:text-white transition-colors`}></i>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-white mb-2 transition-colors">
                      {subService.title}
                    </h3>
                    <p className="text-slate-500 group-hover:text-white/60 text-sm leading-relaxed transition-colors">
                      {subService.description}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-20 lg:py-28 bg-slate-900" style={{contentVisibility: 'auto', containIntrinsicSize: '0 500px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-0.5 bg-amber-500"></div>
              <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">How It Works</span>
              <div className="w-8 h-0.5 bg-amber-500"></div>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white max-w-xl mx-auto leading-tight">
              Our Proven Process
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 hover:border-amber-500/40 transition-all duration-300 h-full">
                  <div className="text-5xl font-bold text-amber-500/25 mb-5 leading-none">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{step.description}</p>
                </div>
                {index < service.processSteps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 w-6 h-6 items-center justify-center">
                    <i className="ri-arrow-right-line text-xl text-amber-500/60"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="py-20 lg:py-28 bg-white" style={{contentVisibility: 'auto', containIntrinsicSize: '0 600px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5 bg-amber-500"></div>
                <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">FAQ</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-4">
                Common Questions Answered
              </h2>
              <p className="text-slate-500 text-base leading-relaxed">
                Can't find what you're looking for? Reach out to our team directly.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-6 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
              >
                Contact Us
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="lg:col-span-8 space-y-3">
              {service.faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                    openFaq === index
                      ? 'border-amber-400 shadow-md shadow-amber-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-base font-semibold text-slate-900 pr-4">
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${
                      openFaq === index ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-base`}></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 pt-1">
                      <p className="text-slate-600 leading-relaxed text-sm">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Other Services ── */}
      <section className="py-20 lg:py-24 bg-slate-50" style={{contentVisibility: 'auto', containIntrinsicSize: '0 500px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-0.5 bg-amber-500"></div>
                <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Explore More</span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">Related Services</h2>
            </div>
            <Link
              to="/services"
              className="hidden sm:inline-flex items-center gap-2 text-slate-600 hover:text-amber-600 font-semibold transition-colors whitespace-nowrap"
            >
              View All Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                className="group relative block h-56 rounded-2xl overflow-hidden bg-slate-900 hover:scale-[1.02] hover:shadow-xl transition-all duration-300"
              >
                <img
                  src={s.image}
                  alt={`${s.title} — CJ Solutions Cyprus`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                  <h3 className="text-base font-bold text-white leading-tight">{s.title}</h3>
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line text-white text-sm"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-20 lg:py-24 bg-white" style={{contentVisibility: 'auto', containIntrinsicSize: '0 400px'}}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative bg-slate-900 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://public.readdy.ai/ai/img_res/4469d931c943f76877e930c75d33caf9.jpg"
                alt="CJ Solutions — Compliance and Licensing Consultants in Cyprus"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute left-0 top-0 h-full w-1.5 bg-amber-500"></div>
            <div className="relative px-10 lg:px-16 py-14 lg:py-16 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                  Ready to Move Forward?
                </h2>
                <p className="text-white/65 text-lg max-w-xl">
                  Our experts are ready to guide you through every step. Get a tailored consultation at no cost.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/30 whitespace-nowrap"
                >
                  Book Free Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/25 hover:border-white/60 text-white font-semibold rounded-xl transition-all duration-300 whitespace-nowrap"
                >
                  All Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
